/**
 * Agent Authorization Contract
 *
 * Issue #261: AI-Powered Data Chat Enhancement - Phase 7 (Agent Authorization & Security)
 *
 * Defines interfaces for agent authorization:
 * - Tool access control
 * - Resource permissions
 * - Scope-based authorization
 * - Audit logging
 */

import { z } from 'zod';

// ============================================================================
// ENUMS
// ============================================================================

export enum AgentPermission {
  // Read operations
  READ_CASES = 'read:cases',
  READ_CONSUMERS = 'read:consumers',
  READ_LEADS = 'read:leads',
  READ_USERS = 'read:users',
  READ_BILLING = 'read:billing',
  READ_ANALYTICS = 'read:analytics',
  READ_DOCUMENTS = 'read:documents',

  // Write operations
  WRITE_CASES = 'write:cases',
  WRITE_CONSUMERS = 'write:consumers',
  WRITE_LEADS = 'write:leads',
  WRITE_USERS = 'write:users',
  WRITE_BILLING = 'write:billing',
  WRITE_DOCUMENTS = 'write:documents',

  // Execute operations
  EXECUTE_TOOLS = 'execute:tools',
  EXECUTE_WORKFLOWS = 'execute:workflows',
  EXECUTE_REPORTS = 'execute:reports',

  // Admin operations
  ADMIN_AGENTS = 'admin:agents',
  ADMIN_SETTINGS = 'admin:settings',
}

export enum ToolCategory {
  READ = 'READ',
  WRITE = 'WRITE',
  DELETE = 'DELETE',
  EXECUTE = 'EXECUTE',
  ADMIN = 'ADMIN',
}

export enum AuthzDecision {
  ALLOW = 'ALLOW',
  DENY = 'DENY',
  REQUIRE_APPROVAL = 'REQUIRE_APPROVAL',
}

// ============================================================================
// CORE TYPES
// ============================================================================

/**
 * Tool definition with authorization metadata
 */
export interface AuthorizedTool {
  name: string;
  description: string;
  category: ToolCategory;
  requiredPermissions: AgentPermission[];
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  requiresApproval: boolean;
  resourceType?: string;
  auditRequired: boolean;
}

/**
 * Agent authorization context
 */
export interface AgentAuthzContext {
  agentId: string;
  businessId: string;
  userId: string;
  sessionId: string;
  permissions: AgentPermission[];
  scopes: string[];
  metadata?: Record<string, unknown>;
}

/**
 * Authorization request
 */
export interface AuthzRequest {
  context: AgentAuthzContext;
  tool: string;
  resourceType?: string;
  resourceId?: string;
  action: 'read' | 'write' | 'delete' | 'execute';
  parameters?: Record<string, unknown>;
}

/**
 * Authorization response
 */
export interface AuthzResponse {
  decision: AuthzDecision;
  reason?: string;
  requiredApproval?: {
    type: string;
    approvers?: string[];
    timeout?: number;
  };
  restrictions?: {
    fields?: string[];
    maxResults?: number;
    filters?: Record<string, unknown>;
  };
  auditId?: string;
}

/**
 * Permission policy
 */
export interface PermissionPolicy {
  id: string;
  name: string;
  description?: string;
  permissions: AgentPermission[];
  scopes: string[];
  conditions?: PolicyCondition[];
  priority: number;
}

/**
 * Policy condition
 */
export interface PolicyCondition {
  field: string;
  operator: 'eq' | 'ne' | 'in' | 'contains' | 'matches';
  value: unknown;
}

/**
 * Audit log entry
 */
export interface AuthzAuditEntry {
  id: string;
  timestamp: Date;
  agentId: string;
  businessId: string;
  userId: string;
  sessionId: string;
  tool: string;
  action: string;
  resourceType?: string;
  resourceId?: string;
  decision: AuthzDecision;
  reason?: string;
  parameters?: Record<string, unknown>;
  result?: 'success' | 'failure';
  durationMs?: number;
}

// ============================================================================
// REQUEST/RESPONSE TYPES
// ============================================================================

export interface CheckPermissionRequest {
  context: AgentAuthzContext;
  permission: AgentPermission;
}

export interface CheckPermissionResponse {
  allowed: boolean;
  reason?: string;
}

export interface ListToolsRequest {
  businessId: string;
  agentId: string;
  category?: ToolCategory;
}

export interface ListToolsResponse {
  tools: AuthorizedTool[];
}

export interface GetPoliciesRequest {
  businessId: string;
  agentId?: string;
}

export interface GetPoliciesResponse {
  policies: PermissionPolicy[];
}

export interface CreatePolicyRequest {
  businessId: string;
  policy: Omit<PermissionPolicy, 'id'>;
}

export interface CreatePolicyResponse {
  policy: PermissionPolicy;
}

export interface GetAuditLogsRequest {
  businessId: string;
  agentId?: string;
  userId?: string;
  fromDate?: Date;
  toDate?: Date;
  limit?: number;
  offset?: number;
}

export interface GetAuditLogsResponse {
  entries: AuthzAuditEntry[];
  total: number;
  hasMore: boolean;
}

// ============================================================================
// DEFAULT TOOL DEFINITIONS
// ============================================================================

export const DEFAULT_AGENT_TOOLS: AuthorizedTool[] = [
  // Case Management
  {
    name: 'search_cases',
    description: 'Search for cases by query',
    category: ToolCategory.READ,
    requiredPermissions: [AgentPermission.READ_CASES],
    riskLevel: 'low',
    requiresApproval: false,
    resourceType: 'case',
    auditRequired: true,
  },
  {
    name: 'get_case',
    description: 'Get details of a specific case',
    category: ToolCategory.READ,
    requiredPermissions: [AgentPermission.READ_CASES],
    riskLevel: 'low',
    requiresApproval: false,
    resourceType: 'case',
    auditRequired: true,
  },
  {
    name: 'update_case',
    description: 'Update case details',
    category: ToolCategory.WRITE,
    requiredPermissions: [AgentPermission.WRITE_CASES],
    riskLevel: 'medium',
    requiresApproval: true,
    resourceType: 'case',
    auditRequired: true,
  },

  // Consumer Management
  {
    name: 'search_consumers',
    description: 'Search for consumers',
    category: ToolCategory.READ,
    requiredPermissions: [AgentPermission.READ_CONSUMERS],
    riskLevel: 'low',
    requiresApproval: false,
    resourceType: 'consumer',
    auditRequired: true,
  },
  {
    name: 'get_consumer',
    description: 'Get consumer details',
    category: ToolCategory.READ,
    requiredPermissions: [AgentPermission.READ_CONSUMERS],
    riskLevel: 'low',
    requiresApproval: false,
    resourceType: 'consumer',
    auditRequired: true,
  },

  // Lead Management
  {
    name: 'search_leads',
    description: 'Search for leads',
    category: ToolCategory.READ,
    requiredPermissions: [AgentPermission.READ_LEADS],
    riskLevel: 'low',
    requiresApproval: false,
    resourceType: 'lead',
    auditRequired: true,
  },
  {
    name: 'update_lead',
    description: 'Update lead status or details',
    category: ToolCategory.WRITE,
    requiredPermissions: [AgentPermission.WRITE_LEADS],
    riskLevel: 'medium',
    requiresApproval: true,
    resourceType: 'lead',
    auditRequired: true,
  },

  // Analytics
  {
    name: 'get_analytics',
    description: 'Get analytics and metrics',
    category: ToolCategory.READ,
    requiredPermissions: [AgentPermission.READ_ANALYTICS],
    riskLevel: 'low',
    requiresApproval: false,
    resourceType: 'analytics',
    auditRequired: false,
  },
  {
    name: 'generate_report',
    description: 'Generate a report',
    category: ToolCategory.EXECUTE,
    requiredPermissions: [AgentPermission.EXECUTE_REPORTS],
    riskLevel: 'low',
    requiresApproval: false,
    resourceType: 'report',
    auditRequired: true,
  },

  // Billing (high risk)
  {
    name: 'get_billing_info',
    description: 'Get billing information',
    category: ToolCategory.READ,
    requiredPermissions: [AgentPermission.READ_BILLING],
    riskLevel: 'medium',
    requiresApproval: false,
    resourceType: 'billing',
    auditRequired: true,
  },
  {
    name: 'update_subscription',
    description: 'Update subscription',
    category: ToolCategory.WRITE,
    requiredPermissions: [AgentPermission.WRITE_BILLING],
    riskLevel: 'critical',
    requiresApproval: true,
    resourceType: 'subscription',
    auditRequired: true,
  },
];

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const agentPermissionSchema = z.nativeEnum(AgentPermission);
export const toolCategorySchema = z.nativeEnum(ToolCategory);
export const authzDecisionSchema = z.nativeEnum(AuthzDecision);

export const agentAuthzContextSchema = z.object({
  agentId: z.string().min(1),
  businessId: z.string().min(1),
  userId: z.string().min(1),
  sessionId: z.string().min(1),
  permissions: z.array(agentPermissionSchema),
  scopes: z.array(z.string()),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const authzRequestSchema = z.object({
  context: agentAuthzContextSchema,
  tool: z.string().min(1),
  resourceType: z.string().optional(),
  resourceId: z.string().optional(),
  toolId: z.string().optional(),
  action: z.enum(['read', 'write', 'delete', 'execute']),
  parameters: z.record(z.string(), z.unknown()).optional(),
});

export const checkPermissionRequestSchema = z.object({
  context: agentAuthzContextSchema,
  permission: agentPermissionSchema,
});

export const listToolsRequestSchema = z.object({
  businessId: z.string().min(1),
  agentId: z.string().min(1),
  category: toolCategorySchema.optional(),
});

export const getAuditLogsRequestSchema = z.object({
  businessId: z.string().min(1),
  agentId: z.string().optional(),
  userId: z.string().optional(),
  fromDate: z.date().optional(),
  toDate: z.date().optional(),
  limit: z.number().min(1).max(1000).optional().default(100),
  offset: z.number().min(0).optional().default(0),
});

// ============================================================================
// AGENT IDENTITY TYPES (Issue #261 - Phase 7)
// ============================================================================

/**
 * Scopes that define what an agent can access and do
 */
export enum AgentScope {
  // General access scopes (used for permission validation)
  READ = 'read',
  SEARCH = 'search',
  ANALYZE = 'analyze',
  WRITE = 'write',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  EXECUTE = 'execute',
  ADMIN = 'admin',

  // Resource-specific read scopes
  READ_CASES = 'read:cases',
  READ_CONSUMERS = 'read:consumers',
  READ_LEADS = 'read:leads',
  READ_USERS = 'read:users',
  READ_BILLING = 'read:billing',
  READ_ANALYTICS = 'read:analytics',
  READ_DOCUMENTS = 'read:documents',

  // Resource-specific write scopes
  WRITE_CASES = 'write:cases',
  WRITE_CONSUMERS = 'write:consumers',
  WRITE_LEADS = 'write:leads',
  WRITE_USERS = 'write:users',
  WRITE_BILLING = 'write:billing',
  WRITE_DOCUMENTS = 'write:documents',

  // Execute scopes
  EXECUTE_TOOLS = 'execute:tools',
  EXECUTE_WORKFLOWS = 'execute:workflows',
  EXECUTE_REPORTS = 'execute:reports',
}

/**
 * Tool permission configuration for an agent
 */
export interface ToolPermissionConfig {
  allowed: boolean;
  requiresApproval?: boolean;
  maxCallsPerSession?: number;
  restrictions?: Record<string, unknown>;
}

/**
 * Resource access configuration for an agent
 */
export interface ResourceAccess {
  type: string;
  permission: 'read' | 'write' | 'admin';
  restrictions?: {
    fields?: string[];
    filters?: Record<string, unknown>;
  };
}

/**
 * Agent identity with scopes and permissions
 */
export interface AgentIdentity {
  agentId: string;
  businessId: string;
  sponsorUserId: string;
  scopes: AgentScope[];
  toolPermissions: Record<string, ToolPermissionConfig>;
  resourceAccess: ResourceAccess[];
  createdAt: Date;
  expiresAt?: Date;
  metadata?: Record<string, unknown>;
}

/**
 * Request to create an agent identity
 */
export interface CreateAgentIdentityRequest {
  agentId: string;
  businessId: string;
  sponsorUserId: string;
  scopes: AgentScope[];
  toolPermissions?: Record<string, ToolPermissionConfig>;
  resourceAccess?: ResourceAccess[];
  expiresAt?: Date;
  metadata?: Record<string, unknown>;
}

/**
 * Request to validate agent scopes against a resource
 */
/**
 * Request to validate agent scopes against a resource
 */
export interface ValidateAgentScopeRequest {
  agentId: string;
  businessId: string;
  requiredScopes: AgentScope[];
  resourceType?: string;
  resourceId?: string;
  toolId?: string;
}

/**
 * Response from scope validation
 */
export interface ValidateAgentScopeResponse {
  valid: boolean;
  allowed: boolean;
  missingScopes?: AgentScope[];
  agentScopes?: AgentScope[];
  reason?: string;
}

// Zod schemas for agent identity types
export const agentScopeSchema = z.nativeEnum(AgentScope);

export const createAgentIdentityRequestSchema = z.object({
  agentId: z.string().min(1),
  businessId: z.string().min(1),
  sponsorUserId: z.string().min(1),
  scopes: z.array(agentScopeSchema),
  toolPermissions: z
    .record(
      z.string(),
      z.object({
        allowed: z.boolean(),
        requiresApproval: z.boolean().optional(),
        maxCallsPerSession: z.number().optional(),
        restrictions: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .optional(),
  resourceAccess: z
    .array(
      z.object({
        type: z.string(),
        permission: z.enum(['read', 'write', 'admin']),
        restrictions: z
          .object({
            fields: z.array(z.string()).optional(),
            filters: z.record(z.string(), z.unknown()).optional(),
          })
          .optional(),
      }),
    )
    .optional(),
  expiresAt: z.date().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const validateAgentScopeRequestSchema = z.object({
  agentId: z.string().min(1),
  businessId: z.string().min(1),
  requiredScopes: z.array(agentScopeSchema),
  resourceType: z.string().optional(),
  resourceId: z.string().optional(),
  toolId: z.string().optional(),
});
